/**
 * /approvals — Manager / Admin timesheet Approval queue.
 *
 * Wraps `<ApprovalsPage>`. Visible-in-submenu-gating happens in the
 * `<TimesheetsSubMenu>` (Approval link only renders for users whose
 * tenant role is owner/admin/manager). Page-level access is BE-gated
 * via 403 on the queue endpoint, so a member who lands here via a
 * stale link gets a clean "not authorized" error rather than a partial
 * UI.
 *
 * URL search params:
 *   - `status` : 'submitted' | 'approved' | 'rejected'   (default 'submitted')
 *
 * Auth gate mirrors `routes/timesheet.tsx`.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import type { ApprovalQueueStatus } from '@/api/types-bridge'
import { getAccessToken } from '@/auth/cookie-helpers'
import { ApprovalsPage } from '@/features/time/components/ApprovalsPage'

export type ApprovalRange = 'last7' | 'last30' | 'all' | 'custom'

export interface ApprovalsSearchParams {
  status?: ApprovalQueueStatus
  range?: ApprovalRange
  /** YYYY-MM-DD, only honored when `range === 'custom'`. */
  from?: string
  to?: string
}

const STATUS_VALUES: readonly ApprovalQueueStatus[] = ['submitted', 'approved', 'rejected']
const RANGE_VALUES: readonly ApprovalRange[] = ['last7', 'last30', 'all', 'custom']
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export const Route = createFileRoute('/approvals')({
  component: ApprovalsRoute,
  validateSearch: (search: Record<string, unknown>): ApprovalsSearchParams => {
    const out: ApprovalsSearchParams = {}
    const s = search.status
    if (typeof s === 'string' && (STATUS_VALUES as readonly string[]).includes(s)) {
      out.status = s as ApprovalQueueStatus
    }
    const r = search.range
    if (typeof r === 'string' && (RANGE_VALUES as readonly string[]).includes(r)) {
      out.range = r as ApprovalRange
    }
    const from = search.from
    if (typeof from === 'string' && ISO_DATE_RE.test(from)) out.from = from
    const to = search.to
    if (typeof to === 'string' && ISO_DATE_RE.test(to)) out.to = to
    return out
  },
})

function ApprovalsRoute() {
  const navigate = useNavigate()

  useEffect(() => {
    if (!getAccessToken()) {
      navigate({ to: '/login' })
    }
  }, [navigate])

  if (!getAccessToken()) return null

  return <ApprovalsPage />
}
